const API_BASE_URL = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');

const buildUrl = (path) => `${API_BASE_URL}${path}`;

const requiredParam = (value, name) => {
    const normalized = String(value ?? '').trim();
    if (!normalized) {
        throw new Error(`${name} is required`);
    }
    return normalized;
};

export const buildStockPriceHistoryPath = ({ ticker, startDate, endDate, range } = {}) => {
    const params = new URLSearchParams({
        ticker: requiredParam(ticker, 'ticker').toUpperCase(),
        start_date: requiredParam(startDate, 'startDate'),
        end_date: requiredParam(endDate, 'endDate'),
    });
    if (range) {
        params.set('range', range);
    }

    return `/api/stock-price-history?${params.toString()}`;
};

const parseResponse = async (response) => {
    const text = await response.text();
    const data = text ? (() => {
        try {
            return JSON.parse(text);
        } catch {
            return text;
        }
    })() : null;

    if (!response.ok) {
        const message =
            (data && (data.error || data.message)) ||
            `Request failed with status ${response.status}`;
        const error = new Error(message);
        error.status = response.status;
        error.body = data;
        throw error;
    }

    return data;
};

const authorizedFetch = async (path, token, options = {}) => {
    if (!token) {
        throw new Error('Authentication required');
    }

    const headers = {
        Accept: 'application/json',
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...(options.headers || {}),
        Authorization: `Bearer ${token}`,
    };

    const response = await fetch(buildUrl(path), {
        method: 'GET',
        ...options,
        headers,
    });

    return parseResponse(response);
};

export const getRatingUsage = (token) => authorizedFetch('/api/rating/usage', token);

export const getRatingHistory = (token) => authorizedFetch('/api/rating/history', token);

export const createRating = (symbol, token) =>
    authorizedFetch('/api/rating', token, {
        method: 'POST',
        body: JSON.stringify({ symbol }),
    });

export const createStripeCheckout = (token, { successUrl, cancelUrl }) =>
    authorizedFetch('/api/stripe/checkout', token, {
        method: 'POST',
        body: JSON.stringify({
            success_url: successUrl,
            cancel_url: cancelUrl,
        }),
    });

export const searchTickers = async (query) => {
    const trimmedQuery = String(query ?? '').trim();
    if (!trimmedQuery) {
        return { results: [] };
    }

    const response = await fetch(buildUrl(`/api/tickers/search?q=${encodeURIComponent(trimmedQuery)}`), {
        headers: {
            Accept: 'application/json',
        },
    });

    return parseResponse(response);
};

export const getStockPriceHistory = async ({ ticker, startDate, endDate, range, signal } = {}) => {
    const response = await fetch(
        buildUrl(buildStockPriceHistoryPath({ ticker, startDate, endDate, range })),
        {
            headers: {
                Accept: 'application/json',
            },
            signal,
        },
    );

    return parseResponse(response);
};

export const getPublicCustomMetrics = async (ticker) => {
    const query = ticker ? `?ticker=${encodeURIComponent(ticker)}` : '';
    const response = await fetch(buildUrl(`/api/public/custom-metrics${query}`), {
        headers: {
            Accept: 'application/json',
        },
    });

    return parseResponse(response);
};

export const getPublicStockCustomMetrics = async (ticker) => {
    const response = await fetch(buildUrl(`/api/public/stocks/${encodeURIComponent(ticker)}/metrics`), {
        headers: {
            Accept: 'application/json',
        },
    });

    return parseResponse(response);
};

export const getPublicStockCustomMetricDetail = async (ticker, metricSlug) => {
    const response = await fetch(
        buildUrl(
            `/api/public/stocks/${encodeURIComponent(ticker)}/metrics/${encodeURIComponent(metricSlug)}`,
        ),
        {
            headers: {
                Accept: 'application/json',
            },
        },
    );

    return parseResponse(response);
};

export const getStockBaskets = async (token) => {
    const headers = { Accept: 'application/json' };
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(buildUrl('/api/stock-baskets'), {
        headers,
    });

    return parseResponse(response);
};

export const getStockBasketDetail = async (basketId, token) => {
    const headers = { Accept: 'application/json' };
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(buildUrl(`/api/stock-baskets/${basketId}`), {
        headers,
    });

    return parseResponse(response);
};

export const createBasket = (token, data) =>
    authorizedFetch('/api/stock-baskets', token, {
        method: 'POST',
        body: JSON.stringify(data),
    });

export const getCustomMetricSuggestions = async (ticker, token) => {
    const query = ticker ? `?ticker=${encodeURIComponent(ticker)}` : '';
    return authorizedFetch(`/api/custom-metrics/suggestions${query}`, token);
};

export const scanCustomMetricSuggestions = async ({ ticker, limit = 4 }, token) =>
    authorizedFetch('/api/custom-metrics/suggestions/scan', token, {
        method: 'POST',
        body: JSON.stringify({ ticker, limit }),
    });

export const reviewCustomMetricSuggestion = async (
    ticker,
    suggestionKey,
    { action, reviewedBy, rejectionReason, definition, previewValues } = {},
    token,
) =>
    authorizedFetch(
        `/api/custom-metrics/suggestions/${encodeURIComponent(ticker)}/${encodeURIComponent(suggestionKey)}/review`,
        token,
        {
            method: 'POST',
            body: JSON.stringify({
                action,
                reviewed_by: reviewedBy,
                rejection_reason: rejectionReason,
                definition,
                preview_values: previewValues,
            }),
        },
    );

export const previewCustomMetricSuggestion = async (
    ticker,
    suggestionKey,
    { definition, limit, force } = {},
    token,
) =>
    authorizedFetch(
        `/api/custom-metrics/suggestions/${encodeURIComponent(ticker)}/${encodeURIComponent(suggestionKey)}/preview`,
        token,
        {
            method: 'POST',
            body: JSON.stringify({
                definition,
                limit,
                force,
            }),
        },
    );
